import { lazy, Suspense } from "react";
import { Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { Spin } from "antd";
import Layout from "@/components/Layout"; // eager: the app chrome + the per-page Suspense boundary
import { useAuth } from "@/lib/auth/AuthProvider";

// Route pages are code-split (React.lazy) so the initial bundle no longer ships
// every page — the heavy ones (graph explorer, charts, scan report, markdown)
// only download when their route is first visited. Suspense fallbacks: the
// per-page one lives in Layout (keeps the nav); the top-level one below covers
// the routes rendered outside Layout (login/connect/callback/password).
const FleetPage = lazy(() => import("@/pages/_designer_fleet/ui/FleetPage")); // designer's agate presentation, wired to live /api/fleet
const GraphExplorer = lazy(() => import("@/pages/GraphExplorer"));
const EventViewer = lazy(() => import("@/pages/EventViewer"));
const CostManagement = lazy(() => import("@/pages/CostManagement"));
const Policy = lazy(() => import("@/pages/Policy"));
const ToolFilter = lazy(() => import("@/pages/ToolFilter"));
const McpAccess = lazy(() => import("@/pages/McpAccess"));
const Identity = lazy(() => import("@/pages/Identity"));
const Audit = lazy(() => import("@/pages/Audit"));
const Network = lazy(() => import("@/pages/Network"));
const Settings = lazy(() => import("@/pages/Settings"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const Login = lazy(() => import("@/pages/Login"));
const Connect = lazy(() => import("@/pages/Connect"));
const ChangePassword = lazy(() => import("@/pages/ChangePassword"));
const Operators = lazy(() => import("@/pages/Operators"));
const Users = lazy(() => import("@/pages/Users"));
const Governance = lazy(() => import("@/pages/Governance"));
const AgenticScan = lazy(() => import("@/pages/AgenticScan"));
const McpScanReportPreview = lazy(() => import("@/pages/McpScanReportPreview"));
const Images = lazy(() => import("@/pages/Images"));
const Integrations = lazy(() => import("@/pages/Integrations"));
const Discovery = lazy(() => import("@/pages/Discovery"));

export default function App() {
  // MCP Scan is a runtime-flagged surface (server whoami → mcpScanEnabled): only
  // rc38, the host holding the trust-scan key, ships it. When off, the route
  // redirects so a bookmarked /mcp-scan doesn't dead-end on a blank Layout.
  const { mcpScanEnabled } = useAuth();
  return (
    <Suspense fallback={<div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}><Spin size="large" /></div>}>
    <Routes>
      <Route path="/callback" element={<AuthCallback />} />
      {/* DEV-ONLY: MCP Scan report harness against the checked-in fixture. No auth
          so it previews without a login; not linked in the nav. Remove once the
          live /scans passthrough drives the report. */}
      {import.meta.env.DEV && <Route path="/dev/scan-report" element={<McpScanReportPreview />} />}
      <Route path="/login" element={<Login />} />
      <Route path="/connect" element={<Connect />} />
      <Route element={<RequireAuth />}>
        <Route path="/password" element={<ChangePassword />} />
        <Route element={<Layout />}>
          <Route index element={<Navigate to="/fleet" replace />} />
          {/* Overview removed 2026-09-03 — Host Fleet is the console's home page now. */}
          <Route path="/overview" element={<Navigate to="/fleet" replace />} />
          <Route path="/dashboard" element={<Navigate to="/fleet" replace />} />
          <Route path="/fleet" element={<FleetPage />} />
          <Route path="/images" element={<Images />} />
          <Route path="/agent-activity" element={<GraphExplorer />} />
          {/* Event Viewer — fleet-wide event log (replaced the CCPD Graph Explorer iframe). */}
          <Route path="/explore" element={<EventViewer />} />
          {/* CCPD LLM Cost Management dashboard, embedded in-console. */}
          <Route path="/cost" element={<CostManagement />} />
          <Route path="/graph" element={<Navigate to="/explore" replace />} />
          {/* Sandboxes deprecated 2026-08-21 → redirect to Fleet (SSO) */}
          <Route path="/sandboxes" element={<Navigate to="/fleet" replace />} />
          <Route path="/sandboxes/fleet" element={<Navigate to="/fleet" replace />} />
          <Route path="/policy" element={<Policy />} />
          <Route path="/tool-filter" element={<ToolFilter />} />
          {/* MCP Access (RBAC) — roadmap MOCK (Entra group → tier → server/tool
              grants). Lives beside Policy/Tool Filter; not admin-gated, matching
              those access-config neighbors. */}
          <Route path="/mcp-access" element={<McpAccess />} />
          <Route path="/identity" element={<Identity />} />
          <Route path="/network" element={<Network />} />
          <Route path="/audit" element={<Audit />} />
          <Route path="/settings" element={<Settings />} />
          {/* Admin-only in the API (rbac.go gates /api/v1/users*). RequireAdmin
              keeps a read-only operator off a page that would only 403. */}
          <Route element={<RequireAdmin />}>
            <Route path="/operators" element={<Operators />} />
            {/* Fleet users — admin-gated in the API. */}
            <Route path="/users" element={<Users />} />
            {/* MCP Scan retired 2026-09-16 → its bookmark lands on Agentic Scan. */}
            <Route path="/mcp-scan" element={<Navigate to="/agentic-scan" replace />} />
            {/* Agentic Scan — the scan surface (runtime flag + admin gate). */}
            <Route
              path="/agentic-scan"
              element={mcpScanEnabled ? <AgenticScan /> : <Navigate to="/overview" replace />}
            />
            <Route path="/governance" element={<Governance />} />
            <Route path="/integrations" element={<Integrations />} />
            <Route path="/discovery" element={<Discovery />} />
          </Route>
        </Route>
      </Route>
    </Routes>
    </Suspense>
  );
}

// RequireAuth — gate every authed route. No token → redirect to /login,
// remembering where the user wanted to go so we can return them there
// after sign-in.
//
// REDESIGN PROTOTYPE: the gate is disabled — there is no backend to log
// into, so the login page is dead weight while iterating on the UI.
// Restore the commented block before this branch goes anywhere real.
function RequireAuth() {
  // [BR-09] Auth is now decided by the HttpOnly session cookie, surfaced as
  // `authenticated` once whoami resolves — NOT by a JS token. An unauthenticated
  // visit redirects to /login (remembering where the user was headed).
  const { authenticated, mustChangePassword, identityLoading } = useAuth();
  const location = useLocation();
  // Wait for the first whoami (cookie check) before deciding — otherwise a refresh
  // flashes "unauthenticated" and bounces to /login before the session resolves.
  // Bypassed in local vite dev (no backend), same as the redirect below.
  if (identityLoading && !import.meta.env.DEV) {
    return null;
  }
  // Local dev (vite) has no real login backend, so the gate would trap you at
  // /login. Bypass in dev; enforce in the production build served by the console.
  if (!authenticated && !import.meta.env.DEV) {
    return (
      <Navigate
        to="/login"
        state={{ from: location.pathname + location.search }}
        replace
      />
    );
  }
  // Forced rotation. The SERVER enforces this — a `pwreset` identity is refused
  // everywhere except the change itself — so without this redirect the user would
  // see a console of 403s and no way to act. Gate on identity (authenticated), not
  // a token.
  if (
    authenticated &&
    mustChangePassword &&
    location.pathname !== "/password"
  ) {
    return <Navigate to="/password" replace />;
  }
  return <Outlet />;
}

// RequireAdmin — UI-side gate for admin-only pages. NOT a security control: the
// API refuses these routes for a viewer regardless. This exists so a read-only
// operator gets a redirect instead of a page that renders and then fails.
function RequireAdmin() {
  const { role, identityLoading } = useAuth();
  if (identityLoading) return null;
  if (role !== "admin") return <Navigate to="/fleet" replace />;
  return <Outlet />;
}
